import type { PantryFeature, PantryPersonaObject } from 'types/pantry';

/**
 * Client-side auth for the pantry realtime server (web).
 *
 * The secret is NOT available to the browser — signing happens on the server
 * (`/api/pantry-token`), so AUTH_SECRET never ships to the client. The server
 * also issues the device id (`web:<uuid>`) and keeps it in a signed httpOnly
 * cookie, so this composable never picks or stores an id: it takes the one the
 * token response carries. The pantry's DEVICE_PERSONAS allowlist and feature
 * table are the real gates; this composable renders what they return.
 *
 * When the identity lookup fails (pantry unreachable), it retries with bounded
 * backoff, and again on window focus or online. Any successful token, including
 * the ones a page fetches to connect its socket, refreshes the nav.
 *
 * The identity lookup's token is kept for the first socket connect, so a page
 * load makes one token request instead of two. Every later connect, including
 * all reconnects, fetches a fresh one.
 */

/** Where the browser kept its self-chosen id before the server issued them.
 * The server ignores it now, so it is only cleared. */
const LEGACY_DEVICE_ID_KEY = 'pantry-device-id';
/** The features the last successful lookup returned. It only sizes the nav
 * before the lookup answers and is never trusted as an entitlement. */
const FEATURES_HINT_KEY = 'pantry-features-hint';

interface PantryToken {
  deviceId: string;
  timestamp: number;
  signature: string;
  persona: PantryPersonaObject | null;
  features: PantryFeature[];
}

/** Retry delays double from 2s up to 30s, for at most 6 retries (7 requests,
 * about 90s). After that only focus, online or a page's own connect asks. */
const FIRST_RETRY_MS = 2_000;
const MAX_RETRY_MS = 30_000;
const MAX_RETRIES = 6;
/** Focus and online fire in bursts; one attempt per window is enough. */
const WAKE_THROTTLE_MS = 5_000;
/** Requests share one queue, so a hung request (a blackholed network) would
 * stall every later one, socket connects included. This bounds it; the
 * timeout rejects like any other failure and the normal backoff applies. */
const TOKEN_TIMEOUT_MS = 15_000;

/** The first identity lookup, shared across callers so the nav and the page it
 * frames resolve the identity with one request per page load. A failed lookup
 * schedules its own retries; any later successful token updates the identity,
 * so this promise only gates the first attempt. */
let pending: Promise<void> | null = null;
/** Token requests run one at a time per tab. A browser without a cookie gets
 * one minted by its first successful request, and parallel requests (a retry
 * and a socket connect) would each mint a different id. */
let tokenQueue: Promise<unknown> = Promise.resolve();
/** The identity lookup's token, kept for one socket connect. */
let spareToken: { token: PantryToken; receivedAt: number } | null = null;
/** Well inside the pantry's 5-minute skew window, so client clock drift
 * and a slow handshake cannot push a reused token out of range. */
const SPARE_TOKEN_MAX_AGE_MS = 60_000;
/** True once any token request has succeeded; retries stop for good. */
let identityLoaded = false;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
/** Retries scheduled since the last success or wake. */
let attempts = 0;
let identityInFlight = false;
let lastAttemptAt = 0;
let wakeListening = false;

function requestToken() {
  const next = tokenQueue.then(() =>
    $fetch<PantryToken>('/api/pantry-token', {
      method: 'POST',
      timeout: TOKEN_TIMEOUT_MS,
    }),
  );
  tokenQueue = next.catch(() => {});
  return next;
}

function clearRetry() {
  if (retryTimer) clearTimeout(retryTimer);
  retryTimer = null;
}

export function usePantryAuth() {
  /** The server-issued id, set from the first token response. */
  const deviceId = useState<string | null>('pantry-device-id', () => null);
  const persona = useState<PantryPersonaObject | null>(
    'pantry-persona',
    () => null,
  );
  const features = useState<PantryFeature[]>('pantry-features', () => []);
  /** False until `/me` has answered. Gated links stay hidden until then rather
   * than appearing and being taken away. */
  const resolved = useState('pantry-resolved', () => false);
  /** The last answer's features, so the nav can reserve their space while the
   * lookup runs. Empty for a browser that has never had a successful lookup. */
  const featuresHint = useState<PantryFeature[]>(
    'pantry-features-hint',
    () => [],
  );

  /** Every successful token carries the identity, so each one updates it. */
  async function fetchToken() {
    const token = await requestToken();
    deviceId.value = token.deviceId;
    persona.value = token.persona;
    features.value = token.features;
    resolved.value = true;
    // Only a real answer updates the hint. A failed lookup keeps the last good
    // one, so the next load still reserves the right space.
    window.localStorage.setItem(
      FEATURES_HINT_KEY,
      JSON.stringify(token.features),
    );
    identityLoaded = true;
    clearRetry();
    attempts = 0;
    return token;
  }

  function scheduleRetry() {
    if (identityLoaded || retryTimer || attempts >= MAX_RETRIES) return;
    const delay = Math.min(FIRST_RETRY_MS * 2 ** attempts, MAX_RETRY_MS);
    attempts++;
    retryTimer = setTimeout(() => {
      retryTimer = null;
      void resolveIdentity();
    }, delay);
  }

  /** Only a thrown error (network, 5xx) is a failure. A 200 with no persona is
   * an unregistered device, a real answer, so it never retries. */
  async function resolveIdentity() {
    identityInFlight = true;
    lastAttemptAt = Date.now();
    try {
      const token = await fetchToken();
      // Only the identity lookup leaves a spare, so a reconnect's own fetch
      // never leaves a token behind for itself.
      spareToken = { token, receivedAt: Date.now() };
    } catch (e) {
      console.error('Could not resolve pantry identity', e);
      scheduleRetry();
    } finally {
      identityInFlight = false;
      resolved.value = true;
    }
  }

  /** Focus or online: try once now, restarting the backoff, unless identity
   * has loaded, a lookup is running, or one started under 5s ago. */
  function onWake() {
    if (identityLoaded || identityInFlight) return;
    if (Date.now() - lastAttemptAt < WAKE_THROTTLE_MS) return;
    clearRetry();
    attempts = 0;
    void resolveIdentity();
  }

  /** Server-signed query params for the pantry WebSocket connection, called
   * on every (re)connect. It waits for the identity request first: a browser
   * without a cookie gets one minted there, and parallel first requests would
   * each mint a different id. The first connect reuses the identity token if
   * it is under a minute old; every later connect fetches a fresh one. */
  async function connectionQuery() {
    await (pending ??= resolveIdentity());
    // Take the spare before any other await, so two concurrent connects
    // cannot both use it.
    const spare = spareToken;
    spareToken = null;
    if (spare && Date.now() - spare.receivedAt < SPARE_TOKEN_MAX_AGE_MS) {
      return spare.token;
    }
    return await fetchToken();
  }

  const hasFeature = (feature: PantryFeature) =>
    features.value.includes(feature);

  onMounted(() => {
    window.localStorage.removeItem(LEGACY_DEVICE_ID_KEY);
    // localStorage is user-controlled, so anything but an array is ignored.
    try {
      const hint = JSON.parse(
        window.localStorage.getItem(FEATURES_HINT_KEY) ?? '[]',
      );
      featuresHint.value = Array.isArray(hint) ? hint : [];
    } catch {
      featuresHint.value = [];
    }
    pending ??= resolveIdentity();
    // The state lives as long as the tab, so the listeners are never removed.
    if (!wakeListening) {
      wakeListening = true;
      window.addEventListener('focus', onWake);
      window.addEventListener('online', onWake);
    }
  });

  return {
    deviceId,
    persona,
    features,
    featuresHint,
    hasFeature,
    resolved,
    connectionQuery,
  };
}
